import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useFocusMode } from '../context/FocusModeContext';
import { useExperienceMode } from '../context/ExperienceModeContext';
import { usePathScope } from '../context/PathScopeContext';
import { useCopy } from '../context/CopyContext';
import ArrivalReplay from './ArrivalReplay';
import { PrintButton, PrintHeader } from './PrintButton';
import ToolboxPanel from './ToolboxPanel';
import CreditMeter from './CreditMeter';
import PathContextBar, { usePathBarVisible } from './PathContextBar';
import SubjectBar from './SubjectBar';
import { useTenant } from '../context/TenantContext';
import { 
  Home, FileText, Target, Briefcase, GraduationCap, Trophy,
  CreditCard, User, LogOut, Menu, X, Zap, Brain, BookOpen, Download, Calculator,
  DollarSign, Award, TrendingUp, Users, ChevronDown, ChevronRight, Sun, Moon, Rocket, Focus, FileSearch,
  Shield, Building, LayoutDashboard, BarChart3, AlertCircle, Settings, Wallet, Bell, Lock, Wrench, Headphones, Compass, Map, Beaker, PiggyBank, ListChecks, PenSquare, FileCheck, Sparkles, Calendar, ShieldCheck, Send, Search, Globe
} from 'lucide-react';
import { useState, useEffect, useRef, createContext, useContext } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import GamificationToaster from './GamificationToaster';
import EmailSignupForm from './growth/EmailSignupForm';
import PatriciaCounselor from './PatriciaCounselor';
import ActiveChildPill from './ActiveChildPill';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sequential gating for the "FuturePath Journey" levels, each unlocks the next.
const JOURNEY_GATES = {
  '/app/assessment':  { key: 'know_yourself',    prereq: null,               prereqLabel: null },
  '/app/calculator':  { key: 'design_your_life', prereq: 'know_yourself',     prereqLabel: 'Discover Me' },
  '/app/decision-lab':{ key: 'explore_options',  prereq: 'know_yourself',     prereqLabel: 'Discover Me' },
  '/app/pathway':     { key: 'your_roadmap',     prereq: 'explore_options',   prereqLabel: 'Explore My Future' },
};

const PLAN_LABELS = {
  trial: 'Free Trial',
  plus: 'Plus',
  pro: 'Pro',
  max: 'Max',
  elite: 'Elite',
  enterprise: 'Enterprise',
};

export const Navbar = ({ sectionTabs, activeSection, onTabClick }) => {
  const { isAuthenticated } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/80 backdrop-blur-xl border-b border-slate-700/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 flex-shrink-0">
            <img src="/fp-logo.png" alt="Agentic FuturePath AI" className="h-8 w-8 rounded-lg" />
            <span className="font-outfit font-bold text-white text-lg tracking-tight"><span className="text-cyan-300">AGENTIC</span> FUTUREPATH <span className="text-blue-400">AI</span></span>
          </Link>

          {/* Section tabs removed, nav kept deliberately minimal */}

          {/* Desktop Nav, deliberately minimal */}
          <div className="hidden md:flex items-center gap-6 flex-shrink-0">
            <Link to="/how-it-works" className="text-slate-300 hover:text-white transition-colors duration-200 text-sm">How It Works</Link>
            <Link to="/pricing" className="text-slate-300 hover:text-white transition-colors duration-200 text-sm">Pricing</Link>
            <Link to="/blog" className="text-slate-300 hover:text-white transition-colors duration-200 text-sm" data-testid="nav-insights">Blog</Link>
            {isAuthenticated ? (
              <Link to="/app/dashboard" className="btn-primary text-sm px-6 py-2" data-testid="dashboard-btn">
                Dashboard
              </Link>
            ) : (
              <>
                <Link to="/login" className="text-sm font-medium px-5 py-2 rounded-full border border-white/25 text-white hover:border-cyan-300/70 hover:bg-white/[0.07] transition-colors duration-200" data-testid="login-link">
                  Log in
                </Link>
                <Link to="/pricing" className="btn-primary text-sm px-6 py-2" data-testid="signup-btn">
                  Get Started
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button 
            className="md:hidden text-white p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Nav */}
        {mobileOpen && (
          <div className="md:hidden pb-4 space-y-2">
            <Link to="/how-it-works" className="block px-4 py-2 text-slate-300 hover:text-white">How It Works</Link>
            <Link to="/pricing" className="block px-4 py-2 text-slate-300 hover:text-white">Pricing</Link>
            <Link to="/blog" className="block px-4 py-2 text-slate-300 hover:text-white">Blog</Link>
            {isAuthenticated ? (
              <Link to="/app/dashboard" className="block px-4 py-2 text-blue-400">Dashboard</Link>
            ) : (
              <>
                <Link to="/login" className="block px-4 py-2 text-slate-300">Log in</Link>
                <Link to="/pricing" className="block px-4 py-2 text-blue-400">Get Started</Link>
              </>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export const AppSidebar = ({ mobileOpen, setMobileOpen }) => {
  const { user, logout, getAuthHeader } = useAuth();
  const { branding, clearTenant } = useTenant();
  const { focusModeEnabled, focusedPathName, isNavItemVisible, isNavItemHighlighted, isNavGroupHidden, getCurrentPathConfig, disableFocusMode } = useFocusMode();
  const { hasScope, pathLabel, primaryArchetype, allowedPaths, showAllTools, toggleShowAllTools } = usePathScope();
  const { nav: navCopy, isStudentCopy } = useCopy();
  const { mode: experienceMode, setMode: setExperienceMode } = useExperienceMode();
  const isWhiteLabel = branding?.is_white_label;
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(() => {
    // Every page renders its own AppLayout, so the sidebar remounts on navigation,     // the open section has to survive that.
    try {
      const saved = JSON.parse(localStorage.getItem('fp_nav_expanded'));
      if (Array.isArray(saved)) return saved;
    } catch { /* ignore */ }
    return ['main', 'journey'];
  });
  const [journeyProgress, setJourneyProgress] = useState(null);
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem('futurepath_theme') !== 'light';
  });
  const navRef = useRef(null);
  const activeItemRef = useRef(null);

  // Apply theme class on mount and when darkMode changes
  useEffect(() => {
    document.documentElement.classList.toggle('light-mode', !darkMode);
  }, [darkMode]);

  // Fetch sequential journey progress (students only, parents observe their child)
  useEffect(() => {
    if (!user || user.role === 'parent') { setJourneyProgress(null); return; }
    axios.get(`${API}/journey/progress`, getAuthHeader ? getAuthHeader() : {})
      .then((r) => setJourneyProgress(r.data))
      .catch(() => setJourneyProgress(null));
  }, [user, getAuthHeader, location.pathname]);


  // User type checks
  const isParent = user?.role === 'parent';
  const isCareerProfessional = user?.role === 'career_professional' || user?.role === 'career_changer' || user?.role === 'professional';
  // Gen2 college student: explicitly marked with student_type=college or welcome_archetype=college_student
  const isGen2College = user?.student_type === 'college' || user?.welcome_archetype === 'college_student';
  const isCollegeStudent = isGen2College || user?.education_level === 'some_college' || user?.education_level === 'bachelor' || (user?.age && user.age >= 18);
  const isHighSchoolStudent = user?.role === 'student' && !isCollegeStudent;
  
  // Enterprise role checks
  const enterpriseRoles = ['super_admin', 'org_admin', 'counselor', 'university_career_staff', 'workforce_manager', 'employer_admin', 'employer_recruiter'];
  const isEnterpriseUser = enterpriseRoles.includes(user?.role);
  const isSuperAdmin = user?.role === 'super_admin';
  const isCounselor = user?.role === 'counselor';
  const isEmployer = user?.role === 'employer_admin' || user?.role === 'employer_recruiter';

  // B2B vs B2C is decided by one thing: did this student arrive through a district?
  // District students get compliance framing (their path is a given, requirements are set for
  // them). Self-signup families still choose their own path and see the exploration tools.
  const isDistrictStudent = !!(user?.organization_id || user?.org_id);
  // School Demo Mode only ever applies to the student experience.
  const isSchoolDemo = experienceMode === 'school_demo' && user?.role === 'student';
  
  // Get focus mode path config for styling
  const pathConfig = getCurrentPathConfig();

  // Archetype scoping: when someone has chosen their path, the sidebar only
  // shows the tools that path needs. "Show all tools" restores everything.
  const scopeEligible = !isEnterpriseUser && user?.role !== 'facilitator';
  const scopeApplies = scopeEligible && hasScope && !showAllTools && allowedPaths.length > 0;
  
  // Build navigation based on user type
  const getNavigationGroups = () => {
    // Facilitator Navigation (Agentic FuturePath AI Labs)
    if (user?.role === 'facilitator') {
      return [
        {
          id: 'labs',
          label: 'Agentic FuturePath AI Labs',
          items: [
            { path: '/app/labs', icon: Beaker, label: 'Labs Dashboard' },
          ]
        },
      ];
    }

    // Super Admin Navigation
    if (isSuperAdmin) {
      return [
        {
          id: 'compliance',
          label: 'Privacy & Compliance',
          items: [
            { path: '/admin/privacy-compliance', icon: ShieldCheck, label: 'FERPA & Data Privacy' },
            { path: '/admin/consent-audit', icon: Shield, label: 'Consent Audit Log' },
          ]
        },
        {
          id: 'admin',
          label: 'Super Admin',
          items: [
            { path: '/enterprise', icon: LayoutDashboard, label: 'Dashboard' },
            { path: '/admin/users', icon: Users, label: 'User Management' },
            { path: '/app/admin', icon: Building, label: 'Admin Console' },
            { path: '/enterprise/roster', icon: Users, label: 'All Users' },
          ]
        },
        {
          id: 'growth',
          label: 'Growth & Content',
          items: [
            { path: '/app/admin/content', icon: PenSquare, label: 'Content Studio' },
            { path: '/blog', icon: BookOpen, label: 'View Blog' },
            { path: '/admin/qa-console', icon: FileCheck, label: 'QA Console' },
          ]
        },
        {
          id: 'intelligence',
          label: 'Intelligence',
          items: [
            { path: '/intelligence', icon: BarChart3, label: 'FuturePath Intelligence' },
            { path: '/enterprise/cohorts', icon: BarChart3, label: 'Cohort Analytics' },
            { path: '/enterprise/interventions', icon: AlertCircle, label: 'Interventions' },
          ]
        },
        {
          id: 'system',
          label: 'System',
          items: [
            { path: '/enterprise/support', icon: Headphones, label: 'Support Center' },
            { path: '/enterprise/reports', icon: FileText, label: 'Reports' },
            { path: '/enterprise/audit', icon: Shield, label: 'Audit Logs' },
            { path: '/enterprise/settings', icon: Settings, label: 'Settings' },
          ]
        }
      ];
    }

    // Organization staff: their own tenant, nothing else exists for them
    if (user?.role === 'org_staff') {
      return [
        {
          id: 'org',
          label: user?.organization_name || 'My organization',
          items: [
            { path: '/app/org', icon: Building, label: 'Organization Console' },
          ]
        },
      ];
    }

    // Counselor-specific navigation, their command center IS their overview
    if (isCounselor) {
      return [
        {
          id: 'command',
          label: null,
          items: [
            { path: '/app/pathways/counselor', icon: LayoutDashboard, label: 'My Caseload' },
            { path: '/app/pathways/counselor?tab=requirements', icon: ListChecks, label: 'Requirements' },
            { path: '/app/pathways/counselor?tab=meetings', icon: Calendar, label: 'Meetings' },
            { path: '/app/pathways/counselor?tab=messages', icon: PenSquare, label: 'Messages' },
          ]
        },
        {
          id: 'account',
          label: 'Account',
          items: [
            { path: '/app/notifications', icon: Bell, label: 'Notifications' },
            { path: '/app/profile', icon: User, label: 'Settings' },
          ]
        },
      ];
    }

    // Superintendent / District Admin navigation
    if (user?.role === 'superintendent' || user?.role === 'district_admin') {
      return [
        {
          id: 'district',
          label: null,
          items: [
            { path: '/app/institution/superintendent', icon: LayoutDashboard, label: 'District Overview' },
            { path: '/app/pathways/counselor', icon: Users, label: 'Counselor Command Center' },
          ]
        },
        {
          id: 'compliance_district',
          label: 'Privacy & Compliance',
          items: [
            { path: '/admin/privacy-compliance', icon: ShieldCheck, label: 'FERPA & Data Privacy' },
          ]
        },
        {
          id: 'account',
          label: 'Account',
          items: [
            { path: '/app/profile', icon: User, label: 'Settings' },
          ]
        },
      ];
    }

    // Org Admin / Enterprise Navigation
    if (isEnterpriseUser && !isSuperAdmin && !isEmployer) {
      return [
        {
          id: 'main',
          label: null,
          items: [
            { path: '/enterprise', icon: LayoutDashboard, label: 'Enterprise Home' },
          ]
        },
        {
          id: 'intelligence',
          label: 'Intelligence',
          items: [
            { path: '/intelligence', icon: BarChart3, label: 'FuturePath Intelligence' },
            { path: '/enterprise/cohorts', icon: BarChart3, label: 'Cohort Analytics' },
            { path: '/enterprise/roster', icon: Users, label: 'Student Roster' },
            { path: '/enterprise/interventions', icon: AlertCircle, label: 'Interventions' },
          ]
        },
        {
          id: 'reports',
          label: 'Reports',
          items: [
            { path: '/enterprise/reports', icon: FileText, label: 'Generate Reports' },
          ]
        },
        {
          id: 'account',
          label: 'Account',
          items: [
            { path: '/app/notification-settings', icon: Bell, label: 'Notifications' },
            { path: '/app/billing', icon: Zap, label: 'Plan & Capacity' },
            { path: '/app/welcome', icon: Sparkles, label: 'My FuturePath Guide' },
            { path: '/app/profile-intelligence', icon: Compass, label: 'My Record & Evidence' },
            { path: '/app/profile-intelligence', icon: Compass, label: 'My Record & Evidence' },
          { path: '/app/profile', icon: User, label: 'Settings' },
          ]
        }
      ];
    }

    // Employer Navigation
    if (isEmployer) {
      return [
        {
          id: 'main',
          label: null,
          items: [
            { path: '/enterprise/employer', icon: Building, label: 'Employer Dashboard' },
          ]
        },
        {
          id: 'workforce',
          label: 'Workforce Intelligence',
          items: [
            { path: '/enterprise/workforce', icon: BarChart3, label: 'Workforce Readiness' },
            { path: '/enterprise/role-readiness', icon: Target, label: 'Role Mapping' },
            { path: '/enterprise/ats-alignment', icon: FileText, label: 'ATS Alignment' },
          ]
        },
        {
          id: 'pipeline',
          label: 'Talent Pipeline',
          items: [
            { path: '/enterprise/pipeline', icon: Users, label: 'Candidates' },
          ]
        },
        {
          id: 'account',
          label: 'Account',
          items: [
            { path: '/app/billing', icon: Zap, label: 'Plan & Capacity' },
            { path: '/app/welcome', icon: Sparkles, label: 'My FuturePath Guide' },
            { path: '/app/profile-intelligence', icon: Compass, label: 'My Record & Evidence' },
            { path: '/app/profile-intelligence', icon: Compass, label: 'My Record & Evidence' },
          { path: '/app/profile', icon: User, label: 'Settings' },
          ]
        }
      ];
    }
    
    // Parent Navigation, stripped down. Parents only see their linked
    // student's CDP, notifications, and the counselor booking flow. Nothing
    // else. This is NOT a full agentic FuturePath account.
    if (isParent) {
      return [
        {
          id: 'parent_main',
          label: null,
          items: [
            { path: '/app/parent/child-cdp',      icon: FileText,     label: "My Child's Plan" },
            { path: '/app/tuition-calculator',    icon: Calculator,   label: 'College Cost Calculator' },
            { path: '/app/scholarships',          icon: Award,        label: 'Scholarships' },
            { path: '/app/funding',               icon: Wallet,       label: 'Aid & FAFSA' },
            { path: '/app/counselor-meetings',    icon: Calendar,     label: 'Schedule Counselor' },
            { path: '/app/profile',               icon: User,         label: 'Account' },
          ],
        },
      ];
    }
    
    // Career Reinvention, Professional career transition
    if (isCareerProfessional) {
      return [
        {
          id: 'main',
          label: null,
          items: [
            { path: '/app/dashboard', icon: Home, label: "Dashboard" },
            { path: '/app/goals', icon: Target, label: "Goals & Milestones" },
          ]
        },
        {
          id: 'know',
          label: '1 · Know Yourself',
          items: [
            { path: '/app/assessment', icon: Compass, label: "Strengths & Interests" },
          ]
        },
        {
          id: 'life',
          label: '2 · The Life I Want',
          items: [
            { path: '/app/calculator', icon: Calculator, label: "My Lifestyle Goals" },
            { path: '/app/future-outlook', icon: TrendingUp, label: "My Earnings Outlook" },
          ]
        },
        {
          id: 'explore',
          label: '3 · Education Intelligence',
          items: [
            { path: '/app/pathways', icon: GraduationCap, label: "Education Intelligence" },
            { path: '/app/pathways/compare', icon: BarChart3, label: "Compare My Options" },
            { path: '/app/pathways/counselor', icon: Users, label: "Counselor Caseload" },
            { path: '/app/careers', icon: Briefcase, label: "Career Explorer" },
            { path: '/app/paths/trades', icon: Wrench, label: "Skilled Trades" },
            { path: '/app/paths/entrepreneurship', icon: Rocket, label: "Entrepreneurship" },
            { path: '/app/college-intel', icon: GraduationCap, label: "Colleges" },
            { path: '/app/applications', icon: Send, label: "Applications" },
            { path: '/app/academic-plan', icon: BookOpen, label: "Academic Plan" },
          ]
        },
        {
          id: 'intelligence',
          label: '4 · Skills & Work',
          items: [
            { path: '/app/parent/intelligence', icon: Wallet, label: "Financial Intelligence" },
            { path: '/app/future-of-work', icon: TrendingUp, label: "Career Pulse" },
            { path: '/app/skills-assessment', icon: Target, label: "Skills Assessment" },
            { path: '/app/skills', icon: Brain, label: "My Skills Profile" },
            { path: '/app/transition', icon: Map, label: "Transition Intelligence" },
          ]
        },
        {
          id: 'decide',
          label: '5 · My Plan & Decisions',
          items: [
            { path: '/app/pathway', icon: Wrench, label: "Build My Plan" },
            { path: '/app/decision-lab', icon: Map, label: "Compare My Options" },
            { path: '/app/future-self', icon: Sparkles, label: "My Future Self" },
            { path: '/app/scenario-lab', icon: Beaker, label: "What-If Lab" },
          ]
        },
        {
          id: 'fund',
          label: '6 · Funding & Affordability',
          items: [
            { path: '/app/financial', icon: Wallet, label: "Education Costs & Debt" },
            { path: '/app/tuition-calculator', icon: Calculator, label: "Retraining Costs" },
            { path: '/app/funding', icon: Wallet, label: "Grants & Financial Aid" },
            { path: '/app/learning-roi', icon: Calculator, label: "Cost vs. Earnings" },
          ]
        },
        {
          id: 'hired',
          label: '7 · Career Launch',
          items: [
            { path: '/app/readiness', icon: Zap, label: "Career Readiness" },
            { path: '/app/position', icon: Award, label: "My Brand & Visibility" },
            { path: '/app/connect', icon: Users, label: "Mentors & Network" },
            { path: '/app/resume-ats', icon: FileSearch, label: "Resume ATS Analyzer" },
            { path: '/app/radar', icon: AlertCircle, label: "Opportunity Radar" },
            { path: '/app/launch', icon: Rocket, label: "Launch" },
          ]
        },
        {
          id: 'plan',
          label: '8 · Track & Execute',
          items: [
            { path: '/app/my-plan', icon: ListChecks, label: "My Roadmap" },
            { path: '/app/learn', icon: BookOpen, label: "Learning Portal" },
            { path: '/app/portfolio', icon: BookOpen, label: "Academic Portfolio" },
            { path: '/app/vault', icon: FileText, label: "Documents & Evidence" },
          ]
        },
        {
          id: 'report',
          label: 'My Report',
          items: [
            { path: '/app/my-futurepath', icon: FileText, label: "My FuturePath Report" },
            { path: '/app/report', icon: Download, label: "Export Report" },
          ]
        },
        {
          id: 'account',
          label: 'Account',
          items: [
            { path: '/app/billing', icon: Zap, label: "Plan & Capacity" },
            { path: '/app/privacy-center', icon: Lock, label: "Privacy Center" },
            { path: '/app/welcome', icon: Sparkles, label: "My FuturePath Guide" },
            { path: '/app/profile', icon: User, label: "Settings" },
          ]
        },
      ];
    }
    
    // Student / Young Adult Navigation, Hero's Journey Flow
    // Young Adult adults (18-24) always get Resume ATS + Skills Assessment.
    // K-12 students get them only if isCollegeStudent is true.
    const isYoungAdultTier = ['plus', 'pro'].includes(user?.plan);
    const careerToolsGroup = (isCollegeStudent || isYoungAdultTier) ? [
      { path: '/app/resume-ats', icon: FileSearch, label: 'Resume ATS Analyzer' },
    ] : [];

    const collegePlanningItems = isHighSchoolStudent ? [
      { path: '/app/exam-dates', icon: FileText, label: 'Exam Dates & Prep' },
    ] : [];

    // ── Gen2 College Student Navigation ─────────────────────────────────────
    // 10-step college-to-career flow:
    // My Path → Career Reality → AI Future of Work → Experience Gap →
    // Proof Builder → Strategy Simulator → Opportunities → Network & Brand →
    // Finances → Settings
    if (isGen2College) {
      return [
        { id: 'home', groupIcon: Home, label: 'Dashboard', path: '/app/college-dashboard', items: [] },
        {
          id: 'my_path', groupIcon: Compass, label: 'My Path', path: '/app/my-situation',
          items: [
            { path: '/app/my-situation',    icon: Sparkles,     label: 'My Situation' },
            { path: '/app/assessment',      icon: Compass,      label: 'Self Assessment' },
            { path: '/app/calculator',      icon: Calculator,   label: 'Design My Life' },
          ],
        },
        {
          id: 'reality', groupIcon: Compass, label: 'Career', path: '/app/career-landscape',
          items: [
            { path: '/app/career-landscape',    icon: Compass,      label: 'Explore Careers' },
            { path: '/app/career-intelligence', icon: Target,       label: 'Career Plan' },
          ],
        },
        {
          id: 'ai_fow', groupIcon: Zap, label: 'Career Pulse', path: '/app/future-of-work',
          items: [
            { path: '/app/future-of-work', icon: Zap, label: 'Career Pulse' },
          ],
        },
        {
          id: 'gaps', groupIcon: Target, label: 'Skill / Experience Gap', path: '/app/skills',
          items: [
            { path: '/app/skills',    icon: Brain,       label: 'Skills Assessment' },
            { path: '/app/my-plan',   icon: ListChecks,  label: 'My Roadmap' },
          ],
        },
        {
          id: 'proof', groupIcon: Award, label: 'Proof Builder', path: '/app/proof-builder',
          items: [
            { path: '/app/proof-builder', icon: Award,    label: 'Build Proof' },
            { path: '/app/portfolio',     icon: BookOpen,  label: 'Portfolio' },
          ],
        },
        {
          id: 'strategy', groupIcon: Beaker, label: 'Strategy Simulator', path: '/app/path-simulator',
          items: [
            { path: '/app/path-simulator', icon: Beaker, label: 'Strategy Simulator' },
          ],
        },
        {
          id: 'opportunities', groupIcon: Rocket, label: 'Opportunities', path: '/app/internships',
          items: [
            { path: '/app/internships',  icon: Rocket,   label: 'Internships & Co-ops' },
            { path: '/app/scholarships', icon: Award,    label: 'Scholarships & Funding' },
            { path: '/app/programs',     icon: Trophy,   label: 'Programs & Fellowships' },
          ],
        },
        {
          id: 'brand', groupIcon: Users, label: 'Network & Brand', path: '/app/connect',
          items: [
            { path: '/app/connect',    icon: Users,      label: 'Mentors & Network' },
            { path: '/app/position',   icon: User,       label: 'Brand & LinkedIn' },
            { path: '/app/resume-ats', icon: FileSearch, label: 'Resume Analyzer' },
          ],
        },
        {
          id: 'finances', groupIcon: DollarSign, label: 'Finances', path: '/app/college-financial',
          items: [
            { path: '/app/college-financial', icon: Wallet, label: 'Financial Intelligence' },
            { path: '/app/future-outlook', icon: TrendingUp, label: 'Lifetime Earnings' },
            { path: '/app/tuition-calculator', icon: Calculator, label: 'College Costs' },
          ],
        },
        { id: 'my_strategy', groupIcon: Sparkles, label: 'My Strategy', path: '/app/my-strategy', items: [] },
        {
          id: 'account', label: 'Account',
          items: [
            { path: '/app/profile',        icon: User,  label: 'Settings' },
            { path: '/app/privacy-center', icon: Lock,  label: 'Privacy' },
          ],
        },
      ];
    }

    // ── School Demo Mode ────────────────────────────────────────────────────
    // A presentation layer over the same product: six sections that tell one story,
    // with the intermediary landing pages skipped. Nothing is removed, switching to
    // All Tools restores the full navigation below.
    if (isSchoolDemo) {
      return [
        { id: 'home', groupIcon: Home, label: 'Home', path: '/app/dashboard', items: [] },
        {
          id: 'start_here', groupIcon: Focus, label: 'Start Here', path: '/app/start-here',
          items: [
            { path: '/app/start-here',     icon: Focus,        label: 'Profile Overview' },
            { path: '/app/assessment',     icon: Compass,      label: 'Assessment' },
            { path: '/app/calculator',     icon: Calculator,   label: 'Design My Life' },
          ],
        },
        {
          id: 'plan', groupIcon: Map, label: 'Plan', path: '/app/pathways',
          items: [
            { path: '/app/pathways',           icon: GraduationCap, label: 'Education Path' },
            { path: '/app/academic-alignment', icon: BookOpen,     label: 'Academic Alignment' },
            { path: '/app/my-strategy',        icon: Sparkles,     label: 'My Strategy' },
            { path: '/app/my-cdp',             icon: Target,        label: 'Career Development Plan' },
          ],
        },
        {
          id: 'requirements', groupIcon: ListChecks, label: 'Requirements', path: '/app/graduation-readiness',
          items: [
            { path: '/app/graduation-readiness', icon: GraduationCap, label: 'Graduation' },
            { path: '/app/nc-wbl',               icon: Briefcase,     label: 'Work-Based Learning' },
            { path: '/app/exam-dates',           icon: Calendar,      label: 'Exams & Tests' },
            { path: '/app/nc-cte',               icon: Wrench,        label: 'CTE Pathway' },
            { path: '/app/credentials',          icon: Award,         label: 'Credentials' },
          ],
        },
        {
          id: 'explore', groupIcon: Compass, label: 'Explore', path: '/app/careers',
          items: [
            { path: '/app/careers',                icon: Briefcase,     label: 'Careers' },
            { path: '/app/career-intelligence',    icon: TrendingUp,    label: 'Career Intelligence' },
            { path: '/app/career-landscape',       icon: Globe,         label: 'Career Landscape' },
            { path: '/app/future-of-work',         icon: Zap,           label: 'Career Pulse' },
            { path: '/app/college-intel',          icon: GraduationCap, label: 'Colleges' },
            { path: '/app/applications',           icon: Send,          label: 'Applications' },
            { path: '/app/academic-plan',          icon: BookOpen,      label: 'Academic Plan' },
            { path: '/app/paths/trades',           icon: Wrench,        label: 'Trades' },
            { path: '/app/paths/military',         icon: Shield,        label: 'Military' },
            { path: '/app/paths/entrepreneurship', icon: Rocket,        label: 'Entrepreneurship' },
            { path: '/app/paths/workforce',        icon: Building,      label: 'Workforce' },
          ],
        },
        {
          id: 'proof', groupIcon: Award, label: 'Build Proof', path: '/app/proof-builder',
          items: [
            { path: '/app/proof-builder', icon: Award,    label: 'Proof Builder' },
            { path: '/app/portfolio',     icon: BookOpen,  label: 'Portfolio' },
          ],
        },
        {
          id: 'finances', groupIcon: DollarSign, label: 'Finances', path: '/app/financial',
          items: [
            { path: '/app/financial',          icon: Wallet,     label: 'Financial Profile' },
            { path: '/app/college-financial',  icon: BarChart3,  label: 'Financial Intelligence' },
            { path: '/app/future-outlook',     icon: TrendingUp, label: 'Lifetime Earnings' },
            { path: '/app/tuition-calculator', icon: Calculator, label: 'Education Costs' },
            { path: '/app/funding',            icon: PiggyBank,  label: 'Aid & FAFSA' },
          ],
        },
        {
          id: 'opportunities', groupIcon: Briefcase, label: 'Opportunities', path: '/app/internships',
          items: [
            { path: '/app/internships',        icon: Rocket,      label: 'Internships' },
            { path: '/app/scholarships',       icon: Award,       label: 'Scholarships' },
            { path: '/app/programs',           icon: Trophy,      label: 'Programs & Competitions' },
            { path: '/app/connect',            icon: Users,       label: 'Mentors & Network' },
            { path: '/app/events',             icon: Calendar,    label: 'Events' },
            { path: '/app/radar',              icon: AlertCircle, label: 'Opportunity Radar' },
            ...(isDistrictStudent ? [
              { path: '/app/counselor-meetings', icon: Calendar,  label: 'Book Counselor Time' },
            ] : []),
          ],
        },
      ];
    }

    const studentNav = [
      {
        id: 'home',
        groupIcon: Home,
        label: 'Home',
        path: '/app/dashboard',
        items: [],
      },
      {
        id: 'start_here',
        groupIcon: Focus,
        label: 'Start Here',
        path: '/app/assessment',
        items: [
          { path: '/app/assessment',   icon: Compass,    label: 'Assessment' },
          { path: '/app/calculator',   icon: Calculator, label: 'Design My Life' },
          // A self-signup family chooses their own path; a district student is already in one.
          ...(isDistrictStudent ? [] : [
            { path: '/app/choose-path', icon: Map,      label: 'Choose My Path' },
            { path: '/app/future-self', icon: Sparkles, label: 'Meet My Future Self' },
          ]),
        ],
      },
      {
        id: 'plan',
        groupIcon: Map,
        label: 'Plan',
        path: '/app/pathways',
        items: [
          ...(isDistrictStudent ? [
            { path: '/app/counselor-meetings', icon: Calendar, label: 'Book Counselor Time' },
          ] : []),
          { path: '/app/pathways',       icon: GraduationCap, label: 'Education Paths' },
          { path: '/app/academic-alignment', icon: BookOpen,   label: 'Academic Alignment' },
          { path: '/app/scenario-lab',   icon: Beaker,        label: 'What-If Scenarios' },
          { path: '/app/decision-lab',   icon: Compass,       label: 'Decision Lab' },
          { path: '/app/my-strategy',    icon: Sparkles,      label: 'My Strategy' },
        ],
      },
      {
        id: 'requirements',
        groupIcon: ListChecks,
        label: 'Requirements',
        path: '/app/graduation-readiness',
        items: [
          { path: '/app/graduation-readiness', icon: GraduationCap, label: 'Graduation' },
          // The CDP and CTE pathway are district instruments, a self-signup family has no
          // district plan to comply with.
          ...(isDistrictStudent ? [
            { path: '/app/my-cdp', icon: Target, label: 'My Pathway (CDP)' },
          ] : []),
          { path: '/app/exam-dates',           icon: Calendar,      label: 'Exams & Tests' },
          ...(isDistrictStudent ? [
            { path: '/app/nc-cte', icon: Wrench, label: 'CTE Pathways' },
          ] : []),
          { path: '/app/credentials',          icon: Award,         label: 'Credentials' },
        ],
      },
      {
        id: 'explore',
        groupIcon: Compass,
        label: 'Explore',
        path: '/app/careers',
        items: [
          { path: '/app/careers',                icon: Briefcase,     label: 'Careers' },
          { path: '/app/career-intelligence',    icon: TrendingUp,    label: 'Career Intelligence' },
          { path: '/app/career-landscape',       icon: Globe,         label: 'Career Landscape' },
          { path: '/app/future-of-work',         icon: Zap,           label: 'Career Pulse' },
          { path: '/app/college-intel',          icon: GraduationCap, label: 'Colleges' },
          { path: '/app/applications',           icon: Send,          label: 'Applications' },
          { path: '/app/academic-plan',          icon: BookOpen,      label: 'Academic Plan' },
          { path: '/app/pathways/compare',       icon: BarChart3,     label: 'Compare Paths' },
          { path: '/app/paths/trades',           icon: Wrench,        label: 'Skilled Trades' },
          { path: '/app/paths/military',         icon: Shield,        label: 'Military' },
          { path: '/app/paths/entrepreneurship', icon: Rocket,        label: 'Entrepreneurship' },
          { path: '/app/paths/workforce',        icon: Building,      label: 'Straight to Work' },
        ],
      },
      {
        id: 'proof',
        groupIcon: Award,
        label: 'Build Proof',
        path: '/app/proof-builder',
        items: [
          { path: '/app/proof-builder', icon: Award,    label: 'Proof Builder' },
          { path: '/app/portfolio',     icon: BookOpen,  label: 'Portfolio' },
        ],
      },
      {
        id: 'skills',
        groupIcon: Brain,
        label: 'Skills',
        path: '/app/skills',
        items: [
          { path: '/app/skills',            icon: Brain,  label: 'My Skills' },
          { path: '/app/skills-assessment', icon: Target, label: 'Skills Assessment' },
          { path: '/app/learn',             icon: BookOpen, label: 'Learning Portal' },
        ],
      },
      {
        id: 'finances',
        groupIcon: DollarSign,
        label: 'Finances',
        path: '/app/financial',
        items: [
          { path: '/app/financial',          icon: Wallet,     label: 'Financial Profile' },
          { path: '/app/college-financial',  icon: BarChart3,  label: 'Financial Intelligence' },
          { path: '/app/future-outlook',     icon: TrendingUp, label: 'Lifetime Earnings' },
          { path: '/app/tuition-calculator', icon: Calculator, label: 'College Costs' },
          { path: '/app/funding',            icon: PiggyBank,  label: 'Aid & FAFSA' },
        ],
      },
      {
        id: 'opportunities',
        groupIcon: Briefcase,
        label: 'Opportunities',
        path: '/app/internships',
        items: [
          { path: '/app/internships', icon: Rocket,      label: 'Internships' },
          { path: '/app/scholarships', icon: Award,      label: 'Scholarships' },
          { path: '/app/programs',    icon: Trophy,      label: 'Programs & Competitions' },
          { path: '/app/connect',     icon: Users,       label: 'Mentors & Network' },
          { path: '/app/events',      icon: Calendar,    label: 'Events' },
          { path: '/app/nc-wbl',      icon: Briefcase,   label: 'Work-Based Learning' },
          { path: '/app/radar',       icon: AlertCircle, label: 'Opportunity Radar' },
        ],
      },
      {
        id: 'applications',
        groupIcon: FileText,
        label: 'Apply',
        path: '/app/applications',
        items: [
          { path: '/app/applications', icon: ListChecks, label: 'Application Tracker' },
          { path: '/app/portfolio',    icon: BookOpen,   label: 'Academic Portfolio' },
          { path: '/app/resume-ats',   icon: FileSearch, label: 'Resume Analyzer' },
          { path: '/app/rec-letters',  icon: FileText,   label: 'Rec Letters' },
          { path: '/app/readiness',    icon: Target,     label: 'Interview & Readiness' },
          { path: '/app/position',     icon: Award,      label: 'My Brand' },
        ],
      },
      {
        id: 'record',
        groupIcon: FileCheck,
        label: 'My Record',
        path: '/app/my-futurepath',
        items: [
          { path: '/app/my-futurepath', icon: FileText, label: 'FuturePath Report' },
          { path: '/app/vault',         icon: Lock,     label: 'Documents & Evidence' },
        ],
      },
      {
        id: 'account',
        label: 'Account',
        items: [
          { path: '/app/billing',        icon: Zap,     label: 'Plan & Capacity' },
          { path: '/app/privacy-center', icon: Lock,    label: 'Privacy Center' },
          { path: '/app/welcome',        icon: Sparkles, label: 'My FuturePath Guide' },
          { path: '/app/notifications',  icon: Bell,    label: 'Notifications' },
          { path: '/app/profile',        icon: User,    label: 'Settings' },
        ],
      },
    ];

    return studentNav;
  };
  
  // A senior in August is thinking deadlines, tests and money, not exploration.
  // Same structure, ordered for the life they are actually in.
  const STUDENT_ORDER = ['home', 'start_here', 'plan', 'requirements', 'explore', 'skills', 'finances', 'opportunities', 'applications', 'record', 'account'];

  const orderForStudents = (groups) => {
    if (!isStudentCopy) return groups;
    // Apple-style nav (groups have groupIcon), already in correct order, skip reordering
    if (groups[0]?.groupIcon) return groups;
    // Exams and the academic record are what a student actually opens. Pull them out of
    // Execution into their own group near the top, keeping the same routes.
    const deadlineItems = [];
    const rest = groups.map((g) => {
      if (!g.items) return g;
      const keep = [];
      for (const it of g.items) {
        if (['/app/exam-dates'].includes(it.path)) deadlineItems.push(it);
        else keep.push(it);
      }
      return { ...g, items: keep };
    });
    if (deadlineItems.length) {
      rest.push({ id: 'deadlines', label: '2 · Exams & Deadlines', items: deadlineItems });
    }
    const withItems = rest.filter((g) => !g.items || g.items.length);
    const rank = (g) => {
      const i = STUDENT_ORDER.indexOf(g.id);
      return i === -1 ? STUDENT_ORDER.length : i;
    };
    const ordered = [...withItems].sort((a, b) => rank(a) - rank(b));
    // Renumber the labels so the sequence still reads 1, 2, 3.
    let n = 0;
    return ordered.map((g) => {
      if (!g.label || !/^\d+\s·\s/.test(g.label)) return g;
      n += 1;
      return { ...g, label: g.label.replace(/^\d+\s·\s/, `${n} · `) };
    });
  };

  const navigationGroups = orderForStudents(getNavigationGroups());

  // Some routes live under another group's prefix (/app/pathways/compare sits under
  // /app/pathways). An exact match always wins so only one section reads as active.
  const exactGroupId = navigationGroups.find((g) =>
    g.items?.some((i) => (i.path || '').split('?')[0].replace(/\/+$/, '') === location.pathname.replace(/\/+$/, ''))
  )?.id;

  // Keep the active navigation group expanded based on current path
  useEffect(() => {
    const currentPath = location.pathname;
    
    // Find which group contains the current path
    for (const group of navigationGroups) {
      const hasActiveItem = group.items?.some(item => (item.path || '').split('?')[0] === currentPath);
      if (hasActiveItem && !expandedGroups.includes(group.id)) {
        setExpandedGroups([group.id]);
        localStorage.setItem('fp_nav_expanded', JSON.stringify([group.id]));
        break;
      }
    }
    
    // Scroll active item into view after a short delay to allow DOM update
    setTimeout(() => {
      if (activeItemRef.current) {
        activeItemRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  }, [location.pathname]);

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => {
      const next = prev.includes(groupId) ? [] : [groupId];
      localStorage.setItem('fp_nav_expanded', JSON.stringify(next));
      return next;
    });
  };

  const toggleTheme = () => {
    const newTheme = !darkMode;
    setDarkMode(newTheme);
    localStorage.setItem('futurepath_theme', newTheme ? 'dark' : 'light');
    document.documentElement.classList.toggle('light-mode', !newTheme);
  };

  const handleLogout = () => {
    const tenantSlug = localStorage.getItem('tenant_slug');
    logout();
    if (tenantSlug) {
      navigate(`/login?tenant=${tenantSlug}`);
    } else {
      navigate('/');
    }
  };

  // Close mobile sidebar on navigation
  useEffect(() => {
    if (setMobileOpen) setMobileOpen(false);
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div 
          className="fixed inset-0 bg-[#0a1729]/60 backdrop-blur-sm z-30 md:hidden" 
          onClick={() => setMobileOpen(false)}
          data-testid="sidebar-overlay"
        />
      )}
      <aside
        className={`fixed left-0 top-0 h-screen border-r z-40 flex flex-col transition-all duration-300
          ${collapsed && !mobileOpen ? 'w-16' : 'w-64'}
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}
        style={isWhiteLabel ? {
          background: `linear-gradient(180deg, ${branding.dark_bg || '#0a1628'} 0%, ${branding.primary_color}12 100%)`,
          borderColor: `${branding.primary_color}30`,
        } : {
          background: 'linear-gradient(180deg, #061736 0%, #030C1D 100%)',
          borderColor: isParent ? 'rgba(16,185,129,0.45)' : 'rgba(34, 55, 116, 0.5)',
        }}
      >
      {/* Logo */}
      <div className="h-16 flex items-center px-4 border-b" style={isWhiteLabel ? { borderColor: `${branding.primary_color}30` } : { borderColor: 'rgb(51 65 85 / 0.5)' }}>
        <Link to="/app/dashboard" className="flex items-center gap-2 min-w-0">
          {branding?.logo_url ? (
            <img src={branding.logo_url} alt={branding.app_name} className="h-7 max-w-[160px] object-contain flex-shrink-0" />
          ) : (
            <>
              <img src="/fp-logo.png" alt="Agentic FuturePath AI" className="h-7 w-7 rounded-md flex-shrink-0" />
              {!collapsed && <span className="font-outfit font-bold text-white text-sm tracking-tight"><span className="text-cyan-300">AGENTIC</span> FUTUREPATH <span className="text-blue-400">AI</span></span>}
            </>
          )}
          {!collapsed && isWhiteLabel && branding?.logo_url && <span className="font-outfit font-semibold text-white">{branding.app_name}</span>}
        </Link>
      </div>

      {/* Parent Mode indicator, persistent, non-duplicative (page banners live on child-scoped pages) */}
      {isParent && !collapsed && (
        <div className="px-4 pt-3" data-testid="parent-mode-indicator">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
            <Users className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
            <span className="text-xs font-semibold tracking-wide text-emerald-300 uppercase">Parent View</span>
          </div>
        </div>
      )}

      {/* Plan / path controls, kept to one line each so the nav gets the space */}
      {!collapsed && (isParent || (focusModeEnabled && pathConfig)) && (
        <div className="px-4 py-2 border-b" style={isWhiteLabel ? { borderColor: `${branding.primary_color}30` } : { borderColor: 'rgb(51 65 85 / 0.5)' }}>
          {/* Active Child switcher, only renders for parents */}
          {isParent && (
            <div className="mb-2">
              <ActiveChildPill collapsed={collapsed} />
            </div>
          )}

          {/* Your path and its focus toggle live in the top context bar on every page */}

          {/* Focus Mode Badge */}
          {focusModeEnabled && pathConfig && (            <div className={`mt-3 p-3 rounded-lg bg-gradient-to-r ${pathConfig.gradient} relative overflow-hidden`}>
              <div className="absolute inset-0 bg-[#0a1729]/20" />
              <div className="relative flex items-center gap-3">
                <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                  <Focus className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-white/90">Focus Mode</p>
                  <p className="text-xs text-white/70 truncate">{focusedPathName}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Navigation - Grouped for all user types */}
      <nav className="flex-1 py-4 overflow-y-auto">
        {/* Grouped navigation for all users */}
        {navigationGroups.map((group) => {
          // Hide entire group based on focus mode (e.g., hide College Planning for non-college paths)
          if (!isParent && isNavGroupHidden(group.id)) return null;
          
          // Filter items based on focus mode (only for students)
          const visibleItems = isParent 
            ? group.items 
            : group.items.filter(item => isNavItemVisible((item.path || '').split('?')[0]));
          const scopedItems = scopeApplies
            ? visibleItems.filter(item => allowedPaths.includes((item.path || '').split('?')[0]))
            : visibleItems;
          
          // Don't render group if no visible items (standalone groupIcon links like Home are exempt)
          if (scopedItems.length === 0 && !(group.groupIcon && group.path)) return null;
          
          return (
          <div key={group.id} className="mb-0.5">
            {/* Group Header */}
            {group.groupIcon ? (
              // ── Apple-style nav item ──
              (() => {
                const GroupIcon = group.groupIcon;
                const isStandalone = !group.items?.length;
                const isGroupActive = isStandalone
                  ? (location.pathname === group.path || location.pathname.startsWith((group.path || '') + '/'))
                  : (exactGroupId
                      ? exactGroupId === group.id
                      : (group.items?.some(item => {
                          const p = location.pathname.replace(/\/+$/, '');
                          const t = (item.path || '').split('?')[0].replace(/\/+$/, '');
                          return t && (p === t || (t !== '' && t !== '/' && p.startsWith(t + '/')));
                        }) || false));
                const isExpanded = expandedGroups.includes(group.id);

                if (isStandalone) {
                  return (
                    <Link
                      to={group.path || '/app/dashboard'}
                      className={`flex items-center gap-3 px-4 py-2.5 mx-2 rounded-[10px] transition-all duration-150 ${
                        isGroupActive
                          ? 'text-[#F4F7FF]'
                          : 'text-slate-300 hover:text-white hover:bg-slate-800/60'
                      }`}
                      style={isGroupActive ? {
                        background: 'linear-gradient(90deg, rgba(120,47,225,0.35), rgba(46,134,226,0.18))',
                        border: '1px solid rgba(120,47,225,0.75)',
                        boxShadow: '0 0 14px rgba(120,47,225,0.40), inset 3px 0 0 #39E3DC',
                      } : undefined}
                    >
                      <GroupIcon className="w-4 h-4 flex-shrink-0" style={isGroupActive ? { color: '#39E3DC', filter: 'drop-shadow(0 0 4px rgba(57,227,220,0.6))' } : undefined} />
                      {!collapsed && <span className="text-sm font-medium">{group.label}</span>}
                    </Link>
                  );
                }
                return (
                  <button
                    onClick={() => { toggleGroup(group.id); if (group.path) navigate(group.path); }}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 mx-2 rounded-[10px] transition-all duration-150 text-left ${
                      isGroupActive
                        ? 'text-[#F4F7FF]'
                        : 'text-slate-300 hover:text-white hover:bg-slate-800/60'
                    }`}
                    style={{
                      width: 'calc(100% - 1rem)',
                      ...(isGroupActive ? {
                        background: 'linear-gradient(90deg, rgba(120,47,225,0.35), rgba(46,134,226,0.18))',
                        border: '1px solid rgba(120,47,225,0.75)',
                        boxShadow: '0 0 14px rgba(120,47,225,0.40), inset 3px 0 0 #39E3DC',
                      } : {}),
                    }}
                  >
                    <GroupIcon className="w-4 h-4 flex-shrink-0" style={isGroupActive ? { color: '#39E3DC', filter: 'drop-shadow(0 0 4px rgba(57,227,220,0.6))' } : undefined} />
                    {!collapsed && (
                      <>
                        <span className="text-sm font-medium flex-1 text-left">{group.label}</span>
                        <ChevronRight className={`w-3.5 h-3.5 flex-shrink-0 transition-transform duration-200 ${isExpanded ? 'rotate-90 text-cyan-400' : 'text-slate-300'}`} />
                      </>
                    )}
                  </button>
                );
              })()
            ) : (
              // ── Classic text group header (enterprise / parent nav) ──
              group.label && !collapsed && (
                <button
                  onClick={() => toggleGroup(group.id)}
                  className="w-full flex items-center justify-between px-4 py-2 text-xs font-semibold uppercase tracking-wider"
                  style={isWhiteLabel ? { color: `${branding.secondary_color || branding.primary_color}aa` } : { color: 'rgb(148 163 184)' }}
                >
                  {group.label}
                  {expandedGroups.includes(group.id) ? (
                    <ChevronDown className="w-3 h-3" />
                  ) : (
                    <ChevronRight className="w-3 h-3" />
                  )}
                </button>
              )
            )}
            
            {/* Group Items */}
            {((group.groupIcon ? expandedGroups.includes(group.id) : (group.label === null || expandedGroups.includes(group.id))) || collapsed) && (
              <div className={collapsed ? '' : 'pl-3'}>
                {scopedItems.map((rawItem) => {
                  // Archetype copy can rename a section without changing its route.
                  // Apple-style sub-items keep their own labels (copy overrides collapsed
                  // several distinct steps into the same name).
                  const item = (!group.groupIcon && navCopy[rawItem.path]) ? { ...rawItem, label: navCopy[rawItem.path] } : rawItem;
                  const Icon = item.icon;
                  // Active match: exact path OR nested route under it (e.g. /app/family/123 highlights /app/family).
                  // Special-case "/" so it doesn't match every route.
                  const path = location.pathname.replace(/\/+$/, '');
                  const [targetPath, targetQuery] = (item.path || '').split('?');
                  const target = targetPath.replace(/\/+$/, '');
                  const isActive = targetQuery
                    ? (path === target && location.search.replace(/^\?/, '') === targetQuery)
                    : (target && (
                        path === target ||
                        (!exactGroupId && target !== '' && target !== '/' && path.startsWith(target + '/'))
                      ));
                  const isHighlighted = !isParent && isNavItemHighlighted(targetPath);

                  // Sequential journey gating DISABLED, all journey steps are
                  // always accessible (locking was glitchy and blocked users).
                  const gate = (!isParent && journeyProgress) ? JOURNEY_GATES[item.path] : null;
                  const locked = false;
                  // Skipped (but not genuinely completed) → still accessible, but nudge them back
                  const skippedNotDone = !!(gate && journeyProgress && !locked
                    && (journeyProgress.skipped || []).includes(gate.key)
                    && !(journeyProgress.completed || {})[gate.key]);

                  if (locked) {
                    return (
                      <button
                        key={item.path + item.label}
                        type="button"
                        onClick={() => toast.info(`Complete "${gate.prereqLabel}" first to unlock ${item.label.replace(/^\d+\.\s*/, '')}.`)}
                        data-testid={`nav-locked-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                        title={`Complete "${gate.prereqLabel}" first to unlock`}
                        className="w-full flex items-center gap-3 px-4 py-2.5 mx-2 rounded-lg text-slate-300 hover:bg-slate-800/40 transition-colors text-left"
                      >
                        <Lock className="w-4 h-4 flex-shrink-0" />
                        {!collapsed && (
                          <span className="text-sm flex items-center justify-between gap-2 flex-1 min-w-0">
                            <span className="truncate">{item.label}</span>
                            <span className="text-[10px] uppercase tracking-wide text-slate-300 flex-shrink-0">Locked</span>
                          </span>
                        )}
                      </button>
                    );
                  }

                  return (
                    <Link
                      key={item.path + item.label}
                      to={item.path}
                      ref={isActive ? activeItemRef : null}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      className={`flex items-center ${group.groupIcon ? 'gap-2 px-3 py-1.5' : 'gap-3 px-4 py-2.5'} mx-2 rounded-[10px] transition-all ${
                        isActive 
                          ? (isWhiteLabel ? '' : 'text-[#F4F7FF]')
                          : isHighlighted
                            ? 'text-cyan-400 hover:text-white hover:bg-cyan-800/30 bg-cyan-900/20 border border-cyan-500/20'
                            : 'text-slate-300 hover:text-slate-200 hover:bg-slate-800/50'
                      }`}
                      style={isActive && !isWhiteLabel ? {
                        background: 'linear-gradient(90deg, rgba(120,47,225,0.30), rgba(46,134,226,0.15))',
                        border: '1px solid rgba(57,227,220,0.35)',
                        boxShadow: '0 0 10px rgba(57,227,220,0.20), inset 2px 0 0 #39E3DC',
                      } : isActive && isWhiteLabel ? {
                        background: `${branding.primary_color}20`,
                        color: branding.primary_color,
                        border: `1px solid ${branding.primary_color}40`,
                      } : undefined}
                    >
                      <Icon className={`${group.groupIcon ? 'w-3.5 h-3.5' : 'w-4 h-4'} flex-shrink-0`} style={isActive ? { color: '#39E3DC', filter: 'drop-shadow(0 0 3px rgba(57,227,220,0.5))' } : undefined} />
                      {!collapsed && (
                        <span className={`${group.groupIcon ? 'text-[12px]' : 'text-sm'} flex items-center gap-2 flex-1 min-w-0`}>
                          <span className="truncate">{item.label}</span>
                          {skippedNotDone && (
                            <span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0" title="You skipped this, finish it for your FuturePath Report" data-testid="nav-resume-dot" />
                          )}
                          {isHighlighted && <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-pulse flex-shrink-0" />}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        );
        })}
        {!collapsed && scopeEligible && <ToolboxPanel />}
      </nav>

      {/* User Section */}
      <div className="p-4 border-t" style={isWhiteLabel ? { borderColor: `${branding.primary_color}30` } : { borderColor: 'rgb(51 65 85 / 0.5)' }}>
        {/* View mode, School Demo is a presentation layer, the full product is one click away */}
        {user?.role === 'student' && !collapsed && !isGen2College && (
          <div className="mb-3" data-testid="view-mode-switch">
            <p className="mb-1.5 text-[9.5px] font-semibold uppercase tracking-[0.16em] text-slate-300">View</p>
            <div className="flex gap-1 rounded-lg border border-white/10 bg-white/[0.04] p-1">
              {[['school_demo', 'School Demo'], ['full', 'All Tools']].map(([val, label]) => (
                <button key={val} onClick={() => setExperienceMode(val)}
                  data-testid={`view-mode-${val}`}
                  className={`flex-1 rounded-md px-2 py-1 text-[10.5px] font-semibold transition-colors ${
                    experienceMode === val
                      ? 'bg-cyan-500/[0.18] text-cyan-200 border border-cyan-400/30'
                      : 'text-slate-300 hover:text-white border border-transparent'}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Profile menu, account controls live behind the student's own name */}
        {!collapsed && user?.role === 'student' ? (
          <div className="relative">
            <button onClick={() => setProfileOpen(o => !o)} data-testid="sidebar-profile-btn"
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-slate-200 hover:bg-white/[0.06] transition-colors">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-cyan-400/30 bg-cyan-500/[0.12] text-[11px] font-bold text-cyan-200">
                {(user?.full_name || 'S').split(' ').map(w => w[0]).slice(0, 2).join('')}
              </span>
              <span className="min-w-0 flex-1 truncate text-left text-sm">{user?.first_name || user?.full_name}</span>
              <ChevronDown className={`h-4 w-4 shrink-0 text-slate-300 transition-transform ${profileOpen ? 'rotate-180' : ''}`} />
            </button>
            {profileOpen && (
              <div data-testid="sidebar-profile-menu"
                className="absolute bottom-full left-0 right-0 mb-2 overflow-hidden rounded-xl border border-white/12 backdrop-blur-xl"
                style={{ background: 'rgba(13,27,46,0.96)' }}>
                {[
                  ['/app/profile', 'Profile & Settings', User],
                  ['/app/my-futurepath', 'My Record', FileText],
                  ['/app/notifications', 'Notifications', Bell],
                  ['/app/privacy-center', 'Privacy', Lock],
                  ['/app/billing', 'Plan & Billing', Zap],
                ].map(([to, label, Icon]) => (
                  <Link key={to} to={to} onClick={() => setProfileOpen(false)}
                    data-testid={`profile-menu-${label.split(' ')[0].toLowerCase()}`}
                    className="flex items-center gap-2.5 px-4 py-2.5 text-[13px] text-slate-300 hover:bg-white/[0.06] hover:text-white transition-colors">
                    <Icon className="h-3.5 w-3.5" /> {label}
                  </Link>
                ))}
                <button onClick={handleLogout} data-testid="logout-btn"
                  className="flex w-full items-center gap-2.5 border-t border-white/[0.07] px-4 py-2.5 text-[13px] text-slate-300 hover:bg-red-500/10 hover:text-red-300 transition-colors">
                  <LogOut className="h-3.5 w-3.5" /> Log out
                </button>
              </div>
            )}
          </div>
        ) : (
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="flex items-center gap-3 w-full px-3 py-2 text-slate-300 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
          >
            <LogOut className="w-5 h-5" />
            {!collapsed && <span className="text-sm">Log out</span>}
          </button>
        )}
        {isWhiteLabel && !collapsed && (
          <p className="text-xs text-slate-300 mt-3 text-center">Powered by Agentic FuturePath AI</p>
        )}
      </div>

      {/* Collapse Toggle - desktop only */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="hidden md:flex absolute -right-3 top-20 w-6 h-6 border rounded-full items-center justify-center text-slate-300 hover:text-white"
        style={isWhiteLabel ? {
          background: branding.primary_color,
          borderColor: `${branding.primary_color}60`,
        } : {
          background: 'rgb(51 65 85)',
          borderColor: 'rgb(71 85 105)',
        }}
      >
        {collapsed ? '›' : '‹'}
      </button>
    </aside>
    </>
  );
};

// A page rendered as a tab inside a hub is already inside the chrome. Rather than editing every
// page to know whether it is embedded, the layout notices it is nested and steps out of the way.
const LayoutDepth = createContext(0);

/** Print and page-overview live inside the Your Path bar. They only get their own row
 *  when that bar is not on screen, never two rows, never pushing the page down. */
const PageUtilities = () => {
  const visible = usePathBarVisible();
  const utilities = <><PrintButton /><ArrivalReplay /></>;
  if (visible) return <PathContextBar utilities={utilities} />;
  return (
    <>
      <PathContextBar />
      <div className="flex justify-end items-center gap-2 mb-2 no-print">{utilities}</div>
    </>
  );
};

export const AppLayout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const depth = useContext(LayoutDepth);

  if (depth > 0) return <>{children}</>;

  return (
    <div className="min-h-screen bg-navy-gradient">
      <AppSidebar mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 h-14 bg-slate-900/95 backdrop-blur-xl border-b border-slate-700/50 flex items-center px-4 gap-3">
        <button
          onClick={() => setMobileOpen(true)}
          className="p-2 rounded-lg text-slate-300 hover:text-white hover:bg-slate-700/50 transition-colors"
          data-testid="mobile-menu-btn"
        >
          <Menu className="w-5 h-5" />
        </button>
        <img src="/fp-logo.png" alt="Agentic FuturePath AI" className="h-6 w-6 rounded-md" />
        <span className="font-outfit font-bold text-white text-sm tracking-tight"><span className="text-cyan-300">AGENTIC</span> FUTUREPATH <span className="text-blue-400">AI</span></span>
      </div>
      <main className="md:ml-64 min-h-screen p-4 pt-[4.5rem] md:p-8 md:pt-8 relative"
        style={{ background: 'radial-gradient(circle at 35% 20%, rgba(46,134,226,0.10), transparent 35%), radial-gradient(circle at 80% 50%, rgba(120,47,225,0.08), transparent 30%)' }}>
        {/* Ambient light glows behind content */}
        <div aria-hidden className="pointer-events-none fixed inset-0 md:left-64 -z-10">
          <div className="absolute -top-32 left-1/4 h-[28rem] w-[28rem] rounded-full blur-[120px]"
            style={{ background: 'radial-gradient(circle, rgba(57,227,220,0.12), transparent 70%)' }} />
          <div className="absolute bottom-0 right-0 h-[24rem] w-[24rem] rounded-full blur-[120px]"
            style={{ background: 'radial-gradient(circle, rgba(120,47,225,0.10), transparent 70%)' }} />
          <div className="absolute inset-x-0 top-0 h-px"
            style={{ background: 'linear-gradient(90deg, transparent, rgba(57,227,220,0.45), transparent)' }} />
        </div>
        <SubjectBar />
        <PageUtilities />
        <PrintHeader />
        <LayoutDepth.Provider value={1}>{children}</LayoutDepth.Provider>
      </main>
      <PatriciaCounselor />
      <GamificationToaster />
    </div>
  );
};

export const GlassCard = ({ children, className = '', hover = true, ...props }) => {
  const { branding } = useTenant();
  const isWL = branding?.is_white_label;
  return (
    <div 
      className={`glass-card p-6 ${hover && !isWL ? 'hover:border-blue-400/40 transition-colors' : 'transition-colors'} ${className}`}
      style={hover && isWL ? { '--hover-border': `${branding.primary_color}60` } : undefined}
      onMouseEnter={hover && isWL ? (e) => { e.currentTarget.style.borderColor = `${branding.primary_color}60`; } : undefined}
      onMouseLeave={hover && isWL ? (e) => { e.currentTarget.style.borderColor = ''; } : undefined}
      {...props}
    >
      {children}
    </div>
  );
};

export const StatCard = ({ icon: Icon, label, value, subtext, color = 'blue' }) => {
  const colorClasses = {
    blue: 'text-blue-400',
    cyan: 'text-cyan-400',
    green: 'text-emerald-400',
    amber: 'text-amber-400',
  };

  return (
    <GlassCard className="stat-gradient">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-slate-300 text-sm mb-1">{label}</p>
          <p className={`font-outfit text-3xl font-semibold ${colorClasses[color]}`}>{value}</p>
          {subtext && <p className="text-slate-300 text-sm mt-1">{subtext}</p>}
        </div>
        <div className={`p-3 rounded-lg bg-slate-700/50 ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </GlassCard>
  );
};

export const PageHeader = ({ title, description, action }) => {
  const { branding } = useTenant();
  const isWL = branding?.is_white_label;
  return (
    <div className="flex items-start justify-between mb-8">
      <div>
        <h1 className="font-outfit text-3xl font-semibold text-white mb-2">{title}</h1>
        {description && <p className="text-slate-300">{description}</p>}
        {isWL && (
          <div className="mt-3 h-1 w-20 rounded-full" style={{ background: `linear-gradient(90deg, ${branding.primary_color}, ${branding.secondary_color || branding.primary_color})` }} />
        )}
      </div>
      {action && <div>{action}</div>}
    </div>
  );
};

export const LoadingSpinner = ({ size = 'md' }) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-8 h-8',
    lg: 'w-12 h-12'
  };

  return (
    <div className={`${sizeClasses[size]} border-2 border-slate-700 border-t-blue-500 rounded-full animate-spin`} />
  );
};

export const Footer = () => {
  return (
    <footer className="bg-[#0a1729] border-t border-slate-800/50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-8 border-b border-slate-800">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
          <div>
            <p className="font-outfit text-lg font-semibold text-white">Get FuturePath updates</p>
            <p className="text-sm text-slate-300 mt-1">Early access news, new guides and invitations to demos and pilots.</p>
          </div>
          <EmailSignupForm source="footer" />
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <img src="/fp-logo.png" alt="Agentic FuturePath AI" className="h-7 w-7 rounded-md" />
              <span className="font-outfit font-bold text-white text-sm tracking-tight"><span className="text-cyan-300">AGENTIC</span> FUTUREPATH <span className="text-blue-400">AI</span></span>
            </div>
            <p className="text-slate-300 text-sm">
              Your AI-powered guide to navigating education and career decisions.
            </p>
          </div>
          <div>
            <h4 className="font-outfit font-medium text-white mb-4">Product</h4>
            <div className="space-y-2">
              <Link to="/pricing" className="block text-slate-300 hover:text-white text-sm">Pricing</Link>
              <Link to="/insights" className="block text-slate-300 hover:text-white text-sm" data-testid="footer-insights">FuturePath Insights</Link>
              <Link to="/waitlist" className="block text-cyan-300 hover:text-cyan-200 text-sm" data-testid="footer-waitlist">Join the Waitlist</Link>
              <Link to="/trust" className="block text-slate-300 hover:text-white text-sm">Trust Center</Link>
              <Link to="/security" className="block text-slate-300 hover:text-white text-sm">Security</Link>
            </div>
          </div>
          <div>
            <h4 className="font-outfit font-medium text-white mb-4">Features</h4>
            <div className="space-y-2 text-slate-300 text-sm">
              <p>College Matching</p>
              <p>Scholarship Finder</p>
              <p>Career Insights</p>
              <p>Financial Planning</p>
            </div>
          </div>
          <div>
            <h4 className="font-outfit font-medium text-white mb-4">Support</h4>
            <div className="space-y-2 text-slate-300 text-sm">
              <p>Help Center</p>
              <p>Contact Us</p>
              <a href="/trust" className="block hover:text-white transition-colors">Privacy Policy</a>
              <a href="/trust" className="block hover:text-white transition-colors">Terms of Service</a>
            </div>
          </div>
        </div>
        <div className="mt-12 pt-8 border-t border-slate-800/50 text-center text-slate-300 text-sm">
          © 2026 Agentic FuturePath AI. All rights reserved.
        </div>
      </div>
    </footer>
  );
};
